import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaChevronDown,
  FaUserPlus,
  FaSignOutAlt,
  FaTimes
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { apiGet } from '../../utils/api';
import { assets } from '../../assets/assets';
import NotificationModal from '../employee/NotificationModal';
import { getLogoUrl } from '../../utils/imageUtils';

// Composant Skeleton pour les éléments de la navbar
const SkeletonNavbarElement = ({ className = '', width = '100%', height = '1rem' }) => {
  return (
    <div
      className={`bg-gray-200 rounded-lg ${className}`}
      style={{ 
        width, 
        height,
        background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%)',
        backgroundSize: '1000px 100%',
        animation: 'shimmer 2s infinite'
      }}
    />
  );
};

// Skeleton pour la Navbar
const SkeletonNavbar = ({ isDark }) => {
  return (
    <header className={`shadow-sm border-b sticky top-0 z-30 backdrop-blur-sm ${isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 border-gray-200'}`}>
      <div className="flex items-center justify-end h-16 px-6">
        <div className="flex items-center space-x-3">
          {/* Notification Skeleton */}
          <SkeletonNavbarElement height="2.25rem" width="2.25rem" className="rounded-lg" />
          
          {/* User info Skeleton */}
          <div className="flex items-center space-x-2 pl-3 border-l border-gray-200">
            <div className="hidden sm:block text-right space-y-1">
              <SkeletonNavbarElement height="0.875rem" width="5rem" className="rounded" />
              <SkeletonNavbarElement height="0.75rem" width="4rem" className="rounded" />
            </div>
            {/* Avatar Skeleton */}
            <SkeletonNavbarElement height="2.5rem" width="2.5rem" className="rounded-full" />
            {/* Chevron Skeleton */}
            <SkeletonNavbarElement height="0.75rem" width="0.75rem" className="rounded" />
          </div>
        </div>
      </div>
      
      {/* Styles pour l'animation shimmer */}
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }
      `}</style>
    </header>
  );
};

const NavbarAdminRH = ({ onMenuClick, isCollapsed, siteChanging = false }) => {
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [companyLogoRapport, setCompanyLogoRapport] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [logoNonce, setLogoNonce] = useState(Date.now());
  const accountMenuRef = useRef(null);
  const { user, handleLogout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const userEmail = user?.email || '';
  const managedBy = useMemo(() => {
    const name = (companyName || '').trim();
    return name ? `Vous gérez le compte de ${name}` : '';
  }, [companyName]);

  const displayName = user?.nom_complet || 'AdminRH';
  const firstName = useMemo(() => {
    const raw = (displayName || 'Admin').trim();
    const parts = raw.split(/\s+/).filter(Boolean);
    return parts[0] || raw;
  }, [displayName]);

  const userInitial = useMemo(() => {
    const source = (displayName || userEmail || 'A').trim();
    return source.charAt(0).toUpperCase() || 'A';
  }, [displayName, userEmail]);

  // Charger le logo "rapports" pour l'avatar (une seule fois)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await apiGet('/api/entreprise/settings', { silent: true });
        if (!mounted) return;
        if (response?.success) {
          setCompanyLogoRapport(response?.settings?.logo_rapport || null);
          setCompanyName(response?.settings?.nom_entreprise || '');
          setLogoNonce(Date.now());
        }
      } catch {
        // silent
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Fermer le menu si clic à l'extérieur
  useEffect(() => {
    const onMouseDown = (e) => {
      if (!showAccountMenu) return;
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setShowAccountMenu(false);
      }
    };
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [showAccountMenu]);

  const avatarLogoSrc = useMemo(() => {
    if (!companyLogoRapport) return null;
    return `${getLogoUrl(companyLogoRapport)}?v=${logoNonce}`;
  }, [companyLogoRapport, logoNonce]);

  const handleGoSettings = () => {
    setShowAccountMenu(false);
    navigate('/parametres');
  };

  const handleInviteAdminRhPlus = () => {
    setShowAccountMenu(false);
    navigate('/admin-rh-plus?invite=1');
  };

  const handleLogoutClick = async () => {
    setShowAccountMenu(false);
    await handleLogout();
  };

  // Afficher le skeleton si siteChanging est true
  if (siteChanging) {
    return <SkeletonNavbar isDark={isDark} />;
  }

  return (
    <header className={`shadow-sm border-b sticky top-0 z-30 backdrop-blur-sm ${isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 border-gray-200'}`}>
      <div className="flex items-center justify-end h-16 px-6">
        {/* Right side - Actions */}
        <div className="flex items-center space-x-3">
          {/* Notifications */}
          <div className="relative">
            <button 
              onClick={() => setIsNotificationModalOpen(true)}
              className="flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src={assets.Noti} 
                alt="Notifications" 
                className="w-9 h-9"
              />
            </button>
          </div>
          
          {/* User dropdown */}
          <div className={`relative flex items-center pl-3 border-l ${isDark ? 'border-gray-700' : 'border-gray-200'}`} ref={accountMenuRef}>
            {/* Bouton compte (logo + initial) comme sur l'image */}
            <button
              type="button"
              onClick={() => setShowAccountMenu((v) => !v)}
              className="flex items-center gap-9 px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
              title="Compte"
            >
              {/* Logo entreprise (rapports) */}
              <div className="h-12 max-w-[360px] flex items-center overflow-visible flex-shrink-0">
                {avatarLogoSrc ? (
                  <img
                    src={avatarLogoSrc}
                    alt="Logo entreprise"
                    className="h-12 w-auto object-contain scale-[1.5] origin-left"
                  />
                ) : (
                  <span className="text-xs font-semibold text-gray-700">{companyName || 'Entreprise'}</span>
                )}
              </div>

              {/* Initial utilisateur (cercle) */}
              <div className="h-11 w-11 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
                   style={{ backgroundColor: '#0026E2' }}>
                {userInitial}
              </div>
            </button>

            {/* Menu compte (style Google) */}
            {showAccountMenu && (
              <div className="absolute right-0 top-full mt-3 w-[320px] rounded-3xl shadow-xl border border-gray-200 bg-white overflow-hidden z-50">
                <div className="px-4 pt-3 pb-3 relative">
                  <button
                    type="button"
                    onClick={() => setShowAccountMenu(false)}
                    className="absolute right-2 top-2 h-8 w-8 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors"
                    aria-label="Fermer"
                  >
                    <FaTimes className="text-gray-600" />
                  </button>

                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-900">{userEmail}</p>
                    {managedBy && <p className="text-xs text-gray-600">{managedBy}</p>}
                  </div>

                  <div className="mt-4 flex flex-col items-center">
                    {/* Uniquement l'initial utilisateur (pas de logo) */}
                    <div className="relative">
                      <div
                        className="h-16 w-16 rounded-full flex items-center justify-center text-white text-2xl font-semibold"
                        style={{ backgroundColor: '#0026E2' }}
                      >
                        {userInitial}
                      </div>
                    </div>

                    <p className="mt-3 text-2xl font-medium text-gray-900">Bonjour {firstName} !</p>

                    <button
                      type="button"
                      onClick={handleGoSettings}
                      className="mt-3 px-5 py-2 rounded-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors font-medium text-sm"
                    >
                      Gérer les paramètres
                    </button>
                  </div>
                </div>

                <div className="px-4 pb-3 bg-gray-50">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={handleInviteAdminRhPlus}
                      className="px-5 py-2 rounded-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors font-medium text-sm flex items-center justify-center gap-2"
                    >
                      <FaUserPlus className="text-gray-700 text-base flex-shrink-0" />
                      Inviter
                    </button>
                    <button
                      type="button"
                      onClick={handleLogoutClick}
                      className="px-5 py-2 rounded-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors font-medium text-sm flex items-center justify-center gap-2"
                    >
                      <FaSignOutAlt className="text-gray-700 text-base flex-shrink-0" />
                      Déconnexion
                    </button>
                  </div>

                  <div className="mt-2 flex items-center justify-center gap-3 text-[11px] text-gray-500">
                    <span>Règles de confidentialité</span>
                    <span>•</span>
                    <span>Conditions d&apos;utilisation</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
      />
    </header>
  );
};

export default NavbarAdminRH;
